import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"

import { fetchProperties } from "../../../../../../../../../api/kehrblatt/property-registry/properties"
import {
  findSimilarPropertiesInList,
  formatSimilarAddress,
  hasAddressDuplicateSearchSeed,
  normalizeAddressInput,
  type AddressDuplicateInput,
} from "./_logic/addressDuplicates"

export type { AddressDuplicateInput }
export { normalizeAddressInput }

export const findSimilarProperties = async ({
  organizationId,
  address,
  excludePropertyId = null,
  limit = 50,
}: {
  organizationId?: string | null
  address: AddressDuplicateInput
  excludePropertyId?: string | null
  limit?: number
}) => {
  const normalized = normalizeAddressInput(address)
  const searchTerms = Array.from(
    new Set(
      [normalized.street, normalized.zip, normalized.city, normalized.house_number]
        .map((entry) => (entry ?? "").trim())
        .filter((entry) => entry.length > 1),
    ),
  )

  if (!organizationId || !searchTerms.length) return []

  try {
    const results = await Promise.all(
      searchTerms.map((term) =>
        fetchProperties({
          organizationId,
          search: term,
          limit,
        }),
      ),
    )
    const merged: Record<string, Property> = {}
    results
      .flat()
      .filter(Boolean)
      .forEach((property) => {
        if (!property || (excludePropertyId && property.id === excludePropertyId)) return

        merged[property.id] = property
      })

    return findSimilarPropertiesInList({
      properties: Object.values(merged),
      address: normalized,
      excludePropertyId,
    })
  } catch (err) {
    console.error("address_duplicate_lookup_failed", err)

    return []
  }
}

export const confirmSimilarAddress = async ({
  organizationId,
  address,
  excludePropertyId = null,
  approvalLabel,
}: {
  organizationId?: string | null
  address: AddressDuplicateInput
  excludePropertyId?: string | null
  approvalLabel?: string
}) => {
  const normalized = normalizeAddressInput(address)

  if (!organizationId || !hasAddressDuplicateSearchSeed(normalized)) return true

  const matches = await findSimilarProperties({
    organizationId,
    address: normalized,
    excludePropertyId,
  })

  if (!matches.length) return true

  const preview = matches
    .slice(0, 5)
    .map((property) => formatSimilarAddress(property))
    .filter(Boolean)
  const formattedPreview = preview.length
    ? preview.join(" | ")
    : "Bereits vorhandene Adresse gefunden"
  const description =
    (matches.length === 1
      ? "Es gibt bereits eine ähnliche Adresse: "
      : `Es gibt ${matches.length} ähnliche Adressen: `) +
    `${formattedPreview}. Adresse trotzdem anlegen?`

  const approved = await newApproval({
    title: "Ähnliche Adresse gefunden",
    description,
    approvalLabel: approvalLabel || "Trotzdem anlegen",
    declineLabel: "Abbrechen",
  })

  return Boolean(approved)
}
